import React from 'react';
import {
  Box, Divider, Paper, Stack, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type { Plan } from '../features/plans/plans';
import { SpeedPoint, previewSpeedPoint } from '../features/plans/planCardPoints';

// The signup site's own colours, so the preview reads as the real card.
const GREEN = '#03793E';
const YELLOW = '#F5E748';

// One line with a tick, as the signup card shows it.
const Point = ({ children }: { children: React.ReactNode }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <CheckCircleIcon sx={{
      fontSize: 18, color: YELLOW, bgcolor: GREEN, borderRadius: '50%',
    }}
    />
    <Typography variant="body2" sx={{ fontWeight: 700 }}>{children}</Typography>
  </Stack>
);

// The last line: the speed, with its footnote mark.
const SpeedLine = ({ point }: { point: SpeedPoint }) => (
  <Point>
    {point.label}
    {point.superscript ? <sup>{point.mark}</sup> : point.mark}
    {` ${point.download}/${point.upload}Mbps`}
  </Point>
);

/**
 * The plan as its card looks on the signup site: name, speed, the plan card
 * points with the typical evening speed last, the displayed price and the
 * line under it. It is drawn from the form as it stands, so it changes as
 * the plan is edited. A plan with a promo code shows the price with it, and
 * the code to enter.
 */
const PlanPreviewCard = ({ plan }: { plan: Plan }) => {
  const promo = plan.apply_promo_code && plan.promo_code && plan.promo_cost ? plan.promo_code : '';
  return (
    <Box role="region" aria-label="Signup plan card preview">
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Signup card preview
      </Typography>
      <Paper elevation={3} sx={{ p: 2.5, borderRadius: 2 }}>
        <Typography
          align="center"
          sx={{
            color: GREEN, fontSize: '1.5rem', fontWeight: 500, lineHeight: 1.25, pb: 1,
          }}
        >
          {plan.name.trim() || 'Plan name'}
          <br />
          {`${plan.speed}/${plan.upload > 0 ? plan.upload : plan.speed} Mbps *`}
        </Typography>
        <Divider sx={{ borderColor: GREEN }} />
        <Stack spacing={0.5} sx={{ py: 1.5 }}>
          {(plan.card_points ?? []).map((point, i) => (
            // Two lines can have the same text, thus the key is the position.
            // eslint-disable-next-line react/no-array-index-key
            <Point key={i}>{point}</Point>
          ))}
          <SpeedLine point={previewSpeedPoint(plan)} />
        </Stack>
        <Divider sx={{ borderColor: GREEN }} />
        <Box sx={{ textAlign: 'center', pt: 2 }}>
          <Typography component="span" sx={{ color: GREEN, fontSize: '3.5rem', lineHeight: 1 }}>
            {`$${promo ? plan.promo_cost : plan.cost}`}
          </Typography>
          <Typography component="span" sx={{ color: GREEN, fontSize: '1.75rem', verticalAlign: 'top' }}>
            ^
          </Typography>
          <Typography variant="body2">per month</Typography>
          {promo && (
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {`with promo code ${promo}`}
          </Typography>
          )}
          <Typography variant="body2" sx={{ minHeight: '1.5em', whiteSpace: 'pre-wrap' }}>
            {plan.description1}
          </Typography>
        </Box>
        <Box
          sx={{
            mt: 2, py: 1, borderRadius: 1, bgcolor: GREEN, color: '#fff', textAlign: 'center', fontSize: '1.1rem',
          }}
        >
          Select
        </Box>
      </Paper>
    </Box>
  );
};

export default PlanPreviewCard;
